import { jwtDecode } from 'jwt-decode';

import { productStore } from '@app/components/pages/organizations/organization/products/productDetail/productStore';

export const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';
export const ACCESS_TOKEN_STORAGE_KEY = 'token';

/** Whether this browser has greeted somebody, keyed by user id — see `philBot/onboardingState`. */
export const ONBOARDED_PREFIX = 'phill-onboarded:';

/** A view somebody set for themselves, such as how much of the sprint history a list opens on. */
export const VIEW_PREFERENCE_PREFIX = 'phill-view:';

/** Storage that belongs to this browser rather than to the session, and so outlives a sign-out. */
const KEPT_PREFIXES = [ONBOARDED_PREFIX, VIEW_PREFERENCE_PREFIX];

/**
 * Drop the session, and keep what belongs to the browser rather than to the session.
 *
 * Everything the session put in storage goes. What belongs to the machine stays (see `KEPT_PREFIXES`):
 * the first-login welcome cannot be dismissed, so a wipe that took its flag with it reopens that welcome
 * for somebody who has already seen it, and a remembered view is not the session's to throw away.
 *
 * The rule lives here rather than at each caller because that is where it kept getting lost. Sign-out
 * remembered to put the flags back; the 401 interceptor and the password-reset screen did not — and the 401
 * is the common one, so an expired session was enough to make an un-dismissable dialog reappear.
 */
export const clearSession = (): void => {
  // Held in memory, not storage: the next person signed in on this tab must not start from the last one's figures.
  productStore.clear();
  try {
    const kept = Object.keys(localStorage)
      .filter(key => KEPT_PREFIXES.some(prefix => key.startsWith(prefix)))
      .map(key => [key, localStorage.getItem(key) ?? '1'] as const);

    localStorage.clear();
    kept.forEach(([key, value]) => localStorage.setItem(key, value));
  } catch {
    // Storage unavailable (private mode, blocked cookies), in which case there is no session in it to drop.
  }
};

// The stored access token, or null when there isn't a usable one.
//
// Read through this rather than `getItem` directly: `jwtDecode` throws on anything that is not a JWT,
// and that throw during render leaves a blank page with no way back except clearing storage by hand.
// An unusable value is discarded here so the app falls back to signed-out instead.
export const readAccessToken = (): string | null => {
  const raw = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (!raw) return null;
  try {
    jwtDecode(raw);
    return raw;
  } catch {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    return null;
  }
};
